import styled from 'styled-components';
import {
  currentStreak,
  formatVocalRange,
  learningStreakText,
  sessionsThisWeek,
  voicePartForRange,
  voicePartName,
} from '../../../../common/src/learn_rules.ts';
import { LearnProgress, VoicePart } from '../../../../common/src/model.ts';
import { getLearnPath, LearnPages } from '../../../../common/src/pages.ts';
import { useLearnProgress } from '../../data/use_learn_progress.tsx';
import { useUser } from '../../data/use_user.tsx';
import { useCurrentDate } from '../../util/use_current_date.tsx';
import {
  FindRangeLink,
  findRangePillHeight,
  LearnCard,
  LearnCardList,
  LearnTabBody,
  LearnTabFootnote,
  LearnTabSpacer,
} from './learn_cards.tsx';
import { learnClassCardStatus, learnClasses } from './learn_classes.ts';
import { LearnLink } from './learn_navigation.tsx';
import { shortScreen } from './learn_screen_parts.tsx';

// Stable ids so stories can check that the hub fits its screen without scrolling, and find its cards
export const learnHubId = 'learn-hub';
export const learnClassCardTestId = 'learn-class-card';

// The Learn hub (issue #3470): greeting, range and part, streak and this-week cards, and one card
// per class from learn_classes.ts (issue #3719) - Pitch and Rhythm open their class screens (Pitch
// opens the Range Finder instead until a range is saved), and Harmony, which has no screen yet, is
// a locked "Coming soon" card. Fills the space between the
// header and the footer on one phone screen: the card group and the footnote share the slack
// through flex, and short screens (iPhone SE) get tighter cards through the max-height rule below
// rather than scrolling.
export const LearnHub = () => {
  const { user } = useUser();
  const { progress } = useLearnProgress();
  const today = useCurrentDate();
  const firstName = user?.name.trim().split(/\s+/)[0];
  const streak = currentStreak(progress, today);
  const sessions = sessionsThisWeek(progress, today);

  return (
    <LearnTabBody id={learnHubId}>
      <Greeting>{firstName ? `Hi, ${firstName}` : 'Hi there'}</Greeting>
      <RangeBlock progress={progress} />
      <StatRow>
        <StatCard>
          <StatLabel>Learning Streak</StatLabel>
          <StatValue>{learningStreakText(streak)}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>This week</StatLabel>
          <StatValue>{plural(sessions, 'session')}</StatValue>
        </StatCard>
      </StatRow>
      <LearnCardList>
        {learnClasses.map((info) => {
          const status = learnClassCardStatus(progress, info);
          const Icon = info.icon;
          return (
            <LearnCard
              key={info.id}
              to={status.to}
              icon={<Icon />}
              title={info.name}
              description={info.description}
              status={status.text}
              data-testid={learnClassCardTestId}
              data-class={info.id}
              data-status={status.kind}
            />
          );
        })}
      </LearnCardList>
      <LearnTabSpacer />
      <LearnTabFootnote>Sessions take 2 to 5 minutes. Sundays never break a streak.</LearnTabFootnote>
    </LearnTabBody>
  );
};

function plural(count: number, noun: string): string {
  return `${count} ${count === 1 ? noun : `${noun}s`}`;
}

// "Your part: Bass", the hub's line for a saved range
export function partLine(part: VoicePart): string {
  return `Your part: ${voicePartName(part)}`;
}

// The saved part and range, or the call to action for the Range Finder on its own (issue #3510
// dropped the "About two minutes" line that used to sit under the pill), in the same vertical
// footprint so the cards below do not move when a range is saved
const RangeBlock = ({ progress }: { progress: LearnProgress }) => {
  const { range } = progress;
  if (!range) {
    return (
      <RangeWrapper>
        <FindRangeLink />
      </RangeWrapper>
    );
  }
  const part = progress.voicePart ?? voicePartForRange(range);
  // one line for the part ("Your part: Bass"), which is the way back into the Range Finder to redo
  // it; the range sits under it
  return (
    <RangeWrapper>
      <RangeValue to={getLearnPath(LearnPages.Range)}>{partLine(part)}</RangeValue>
      <RangeSubtitle>{formatVocalRange(range)}</RangeSubtitle>
    </RangeWrapper>
  );
};

const Greeting = styled.div`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 26px;
  line-height: 1.3;

  ${shortScreen} {
    font-size: 22px;
  }
`;

const rangeValueLineHeight = 32;
const rangeSubtitleLineHeight = 22;

// Tall enough for whichever branch is showing - the call-to-action pill on its own, or the part
// line over its range - so the cards below do not move when a range is saved
const rangeBlockMinHeight = Math.max(findRangePillHeight, rangeValueLineHeight + rangeSubtitleLineHeight);

const RangeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: ${rangeBlockMinHeight}px;
  margin-top: 4px;
`;

// A link into the Range Finder, styled as the accent line it always was (no underline), which the
// Range Finder returns from to the hub
const RangeValue = styled(LearnLink)`
  align-self: flex-start;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 24px;
  line-height: ${rangeValueLineHeight}px;
  color: var(--color-tab-selected);
  text-decoration: none;
  -webkit-tap-highlight-color: transparent;
`;

const RangeSubtitle = styled.div`
  font-size: 15px;
  line-height: ${rangeSubtitleLineHeight}px;
  color: var(--color-text-darker);
`;

const StatRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 16px;

  ${shortScreen} {
    margin-top: 10px;
  }
`;

const StatCard = styled.div`
  flex: 1 1 0;
  min-width: 0;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid var(--color-divider);
  display: flex;
  flex-direction: column;

  ${shortScreen} {
    padding: 8px 12px;
  }
`;

const StatLabel = styled.div`
  font-size: 13px;
  line-height: 1.5;
  color: var(--color-text-darker);
`;

const StatValue = styled.div`
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 22px;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${shortScreen} {
    font-size: 19px;
  }
`;
